import { CampaignOutlined } from "@mui/icons-material";
import { Button, Space, Tag, message } from "antd";
import { memo, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "../../../api/api";
import DataTable from "../../../Components/DataTable/DataTable";
import { canAccessSmsCampaign } from "../../../Util/smsCampaignPermissions";
import {
  isSmsCampaignEditable,
  campaignStatusColor,
  campaignStatusLabel,
  formatCampaignSendAt,
  formatJalaliDateTime,
} from "../../../Util/smsCampaign";
import styles from "./SmsCampaigns.module.css";

const SmsCampaigns = ({ canCreate, canEdit, canView }) => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);

  const columns = [
    {
      title: "عنوان",
      dataIndex: "title",
      searchable: true,
    },
    {
      title: "خط",
      dataIndex: "line_number",
      searchable: true,
      render: (_, record) =>
        record.line_number ? <span dir="ltr">{record.line_number}</span> : "—",
    },
    {
      title: "متن",
      dataIndex: "message",
      render: (_, record) => <div className={styles.text}>{record.message}</div>,
    },
    {
      title: "زمان ارسال",
      dataIndex: "send_at",
      render: (_, record) => (
        <span dir="ltr">{formatCampaignSendAt(record.send_at)}</span>
      ),
    },
    {
      title: "وضعیت",
      dataIndex: "status",
      render: (_, record) => (
        <Tag color={campaignStatusColor(record.status)}>
          {campaignStatusLabel(record.status)}
        </Tag>
      ),
    },
    {
      title: "گیرنده‌ها",
      dataIndex: "total",
      render: (_, record) => (
        <div className={styles.recipients}>
          {[
            { key: "total", label: "کل", color: "blue" },
            { key: "pending", label: "انتظار", color: "gold" },
            { key: "sending", label: "در حال ارسال", color: "blue" },
            { key: "sent", label: "ارسال‌شده", color: "green" },
            { key: "failed", label: "ناموفق", color: "red" },
            ...(record.cancelled
              ? [{ key: "cancelled", label: "لغو", color: "default" }]
              : []),
          ].map(({ key, label, color }) => (
            <Tag key={key} color={color} className={styles.recipientStat}>
              <span>{label}</span>
              <b>{Number(record[key] || 0).toLocaleString("fa-IR")}</b>
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: "زمان ایجاد",
      dataIndex: "createdAt",
      render: (_, record) => (
        <span dir="ltr">{formatJalaliDateTime(record.createdAt)}</span>
      ),
    },
    {
      title: "عملیات",
      render: (_, record) => (
        <Space direction="vertical">
          {canView ? (
            <Button onClick={() => navigate("/sms-campaigns/" + record.id)}>
              مشاهده
            </Button>
          ) : null}
          {canEdit && isSmsCampaignEditable(record) ? (
            <Button onClick={() => navigate("/sms-campaigns/" + record.id + "/edit")}>
              ویرایش
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = () => {
    api
      .get("/smscampaign/list")
      .then((res) => {
        setCampaigns(Array.isArray(res.data?.campaigns) ? res.data.campaigns : []);
      })
      .catch((error) => {
        message.error(
          error.response?.data?.error?.message || "خطا در دریافت کمپین‌ها"
        );
      });
  };

  return (
    <DataTable
      id="SmsCampaigns"
      dataSource={campaigns}
      setDataSource={setCampaigns}
      columns={columns}
      reload={loadCampaigns}
      recordCreatorDisabled
      header={{
        title: "کمپین پیامک",
        avatar: { icon: <CampaignOutlined />, size: "large" },
        extra: canCreate ? (
          <Button
            type="primary"
            onClick={() => navigate("/sms-campaigns/create")}
          >
            کمپین جدید
          </Button>
        ) : null,
      }}
    />
  );
};

const mapStateToProps = (state) => {
  const permissions = state.userData ? state.userData.permissions : [];
  return {
    canCreate: canAccessSmsCampaign("create", permissions),
    canEdit: canAccessSmsCampaign("edit", permissions),
    canView: canAccessSmsCampaign("view", permissions),
  };
};

export default connect(mapStateToProps)(memo(SmsCampaigns));
